<script setup lang="ts">
import { SolAlert, SolButton } from '@solfacil/girassol'
import validateFormalizationStep from '~/composables/formalization/useFormalizationStepGuard'
import useFinancing from '~/store-v2/financings/useFinancing'
import useHardwareForm from '~/store-v2/hardware/useHardwareForm'
import useInstallation from '~/store-v2/installation/useInstallation'
import InversorInstallationLabels from './0.InversorInstallationLabels.vue'
import InversorInstallationCredentials from './2.InversorInstallationCredentials.vue'
import InversorInstallationAnalysis from './3.InversorInstallationAnalysis.vue'
import InstallationPendingEquipmentAlert from './components/InstallationPendingEquipmentAlert.vue'
import InverterCommissioningPolling from './components/InverterCommissioningPolling.vue'
import useInstallationCommissioningFlow from './useInstallationCommissioningFlow'
import useInstallationFlow, { steps } from './useInstallationFlow'

const route = useRoute<any>('/installationV2/[id]')
const router = useRouter()
const { t } = useI18n()

const projectId = computed(() => route.params.id as string)

const {
  stepState,
  handleStepNext,
  handleStepPrev,
} = useInstallationFlow(projectId)

const { phase: pollingPhase } = useInstallationCommissioningFlow(projectId)

const {
  isLoading: isLoadingFinancing,
  data: financing,
  isError: isErrorFinancing,
  error: errorFinancing,
} = useFinancing(projectId, false)

const {
  isLoading: isLoadingInstallation,
  data: installation,
  isError: isErrorInstallation,
  error: errorInstallation,
} = useInstallation(projectId)

const {
  isLoading: isLoadingHardwareForm,
} = useHardwareForm(projectId)

const isLoading = computed(() => isLoadingFinancing.value || isLoadingInstallation.value || isLoadingHardwareForm.value)
const isError = computed(() => isErrorFinancing.value || isErrorInstallation.value)
const error = computed(() => errorFinancing.value || errorInstallation.value)

function redirectFormalization() {
  if (financing.value?.person_type === 'PF')
    return router.push(`/customer/${projectId.value}`)

  return router.push(`/company/${projectId.value}`)
}

watch(financing, async (newFinancing) => {
  if (newFinancing?.customer_name) {
    document.title = `Instalação - ${newFinancing?.customer_name} - Solfácil Fintech`
  }

  if (newFinancing && !isLoadingFinancing.value) {
    await nextTick()
    await validateFormalizationStep(newFinancing, projectId.value, route.path, router)
  }
}, { immediate: true })
</script>

<template>
  <!-- Remover isso depois -->
  <!-- <pre>
    Step: {{ stepState }}
    <SolButton id="prev" @click="handleStepPrev">Voltar</SolButton>
    <SolButton id="next" @click="handleStepNext">Avançar</SolButton>
  </pre> -->
  <div v-if="isLoading" class="container">
    <div class="my-4 flex justify-center items-center w-full min-h-96">
      <Spinner size="large" />
    </div>
  </div>

  <div v-else-if="isError">
    {{ error }}
  </div>

  <div v-else-if="installation && financing" class="installation-page">
    <NavigationHeaderBar
      v-if="installation"
      stage="installation"
      :title="installation.project_name"
      :status="installation.status?.slug"
      :financing="financing"
    />
    <InstallationPendingEquipmentAlert
      :project-id="projectId"
      :title="t('customer.equipment_update.alert.pending_title')"
      :description="t('customer.equipment_update.alert.pending_description')"
      class="mb-4xs"
    />

    <SolAlert
      v-if="false"
      id="duplicate-serial-number"
      :title="t('inversorInstallation.form.duplicateSerialNumber.title')"
      feedback="error"
      class="mb-4xs"
    >
      {{ t('inversorInstallation.form.duplicateSerialNumber.description') }}
    </SolAlert>

    <div v-if="false" class="flex flex-col items-start w-full">
      <div class="flex">
        <SolButton id="next" @click="handleStepPrev">
          Voltar
        </SolButton>
      </div>
      <div class="flex">
        <SolButton id="next" @click="handleStepNext">
          Avançar
        </SolButton>
      </div>
    </div>

    <template v-else-if="stepState?.current === steps.LABELS">
      <InversorInstallationLabels />
    </template>
    <template v-else-if="stepState?.current === steps.CREDENTIALS">
      <InversorInstallationCredentials />
    </template>
    <template v-else-if="stepState?.current === steps.POLLING">
      <InverterCommissioningPolling :phase="pollingPhase" />
    </template>
    <template v-else-if="stepState?.current === steps.ANALYSIS">
      <InversorInstallationAnalysis />
    </template>

    <SolButton
      id="installation-back-formalization"
      size="medium"
      class="w-full md:system:w-auto mt-6"
      variant="secondary"
      @click="redirectFormalization"
    >
      {{ t('installationPage.footer.formalization') }}
    </SolButton>
  </div>
</template>

<style scoped lang="scss">
.container {
  @apply bg-neutral-high-pure rounded-lg p-2xs;
}

.installation-page {
  @apply flex flex-col py-2xs px-4xs;

  @screen md:system {
    @apply px-2xs pb-sm pt-2xs;
  }
}
</style>
